import { issuerDisplayName } from "@xop-any-time/shared";
import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";

import { useAuth } from "../../auth/AuthProvider.js";
import { getBranding, getOverview, keys, listLicences } from "../../lib/crmApi.js";
import { count, money } from "../../lib/format.js";
import { CountTiles } from "./overview/CountTiles.js";
import { Ledger } from "./overview/Ledger.js";
import { Nameplate } from "./overview/Nameplate.js";
import { OverduePanel, RenewalsPanel } from "./overview/AttentionPanel.js";

/**
 * The landing page: whose portal this is, where the book stands, what needs
 * doing today.
 *
 * ─────────────────────────────────────────────────────────────────────
 * WHY THIS PAGE IS NOT A ROW OF STAT CARDS ANY MORE
 * ─────────────────────────────────────────────────────────────────────
 * It was four identical cards under a plain heading — the command center's
 * furniture, used unchanged. Two problems with that, and only the second is
 * about looks. The first: four equal boxes say four equal things, when in
 * fact "invoiced" and "overdue" are not peers, and a count you can click
 * into is worth more than one you cannot. The second: this is the only
 * screen in the product that belongs to the RESELLER rather than to XOP, and
 * it opened with a bare `<h1>` of their name.
 *
 * So the page is now three registers, in descending weight:
 *   1. the nameplate + ledger — one surface, laid out like the letterhead on
 *      the invoices this portal issues (see `overview/Nameplate.tsx`);
 *   2. the counts, as tiles that are also the way in;
 *   3. what needs attention, as two short lists of real rows that link to
 *      the thing itself.
 *
 * And nothing else. An earlier pass closed the page with a strip of
 * Documentation / Settings shortcuts; they were the nav rail's own rows,
 * restated two columns to the right of it. The masthead lost a strapline and
 * a "city · currency" line for the same reason — the rail already says what
 * a reseller can reach.
 *
 * ─────────────────────────────────────────────────────────────────────
 * TWO QUERIES, AND THE PAGE SURVIVES EITHER FAILING
 * ─────────────────────────────────────────────────────────────────────
 * The figures come from `/api/crm/overview` (one BFF aggregate over five
 * upstream calls, each degrading on its own). The identity comes from
 * `/api/crm/branding` — the reseller's own trade record, which is also what
 * their PDFs are issued under.
 *
 * Branding is NOT required to render: the session already carries
 * `resellerName`, so a failed branding call costs the logo and the currency
 * SYMBOL — never the name at the top of the page. That matters because
 * branding is the newer endpoint of the two and the page must not go blank
 * the day it hiccups.
 *
 * ─────────────────────────────────────────────────────────────────────
 * THE ONE RULE, INHERITED FROM `lib/format.ts`
 * ─────────────────────────────────────────────────────────────────────
 * A figure the CRM did not supply renders as an em dash, never a zero — and
 * an empty LIST is not the same as an unreachable one (`[]` versus `null`;
 * see `ResellerOverview`). "Nothing overdue" is good news; saying it when
 * the call failed would be a lie told confidently.
 */

/** One staggered entrance for the whole page rather than a dozen scattered
 * micro-animations: each band arrives a beat after the one above, so the eye
 * is walked down the hierarchy exactly once. Transform and opacity only. */
function Band({ children, delay = 0 }: { children: JSX.Element; delay?: number }): JSX.Element {
  return (
    <Box
      sx={{
        "@keyframes overviewRise": {
          from: { opacity: 0, transform: "translateY(8px)" },
          to: { opacity: 1, transform: "none" },
        },
        animation: "overviewRise 420ms cubic-bezier(0.22, 1, 0.36, 1) both",
        animationDelay: `${delay}ms`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    >
      {children}
    </Box>
  );
}

export function OverviewPage(): JSX.Element {
  const { user } = useAuth();

  const { data, isPending, error } = useQuery({
    queryKey: keys.overview,
    queryFn: getOverview,
  });

  /**
   * What runs out next is a LICENCE, and since 2026-09-10 that is its OWN date.
   *
   * It used to be read off the customer's agreement, on the reasoning that an
   * agreement's term IS the licence term. That stopped being true when a licence
   * became one APPLIANCE: a customer with two boxes can hold two licences with
   * two different terms, neither of which need match the agreement. Feeding this
   * panel agreement dates under the heading "Licences expiring" would make the
   * front page assert a date no other surface agrees with — the exact
   * disagreement the licensing design exists to prevent.
   *
   * `expiring: true` narrows it to the 60-day window upstream, so this is a
   * small call rather than the whole subtree.
   */
  const { data: licenceData, error: licenceError } = useQuery({
    queryKey: keys.licences({ expiring: true }),
    queryFn: () => listLicences({ expiring: true }),
  });

  // Identity. `retry: false` because a reseller who has filled in nothing is
  // a normal state, not a transient one worth three round trips.
  const { data: brandingData } = useQuery({
    queryKey: keys.branding,
    queryFn: getBranding,
    retry: false,
  });
  const branding = brandingData?.branding ?? null;

  // The same name their invoices are issued under — legal name when they
  // have set one, else the name XOP knows them by. Falls back to the session
  // so the masthead stands even with no branding record at all.
  const name = branding ? issuerDisplayName(branding) : (user?.resellerName ?? "Overview");
  const currency = branding?.currency ?? data?.currency ?? null;

  const overdueTotal = data?.overdueTotal ?? null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {error && (
        <Box
          role="alert"
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "error.main",
            px: 2,
            py: 1.5,
            fontSize: 13,
            color: "error.main",
          }}
        >
          {(error as Error).message}
        </Box>
      )}

      {/* ── The masthead: who, and how much ─────────────────────────── */}
      <Band>
        <Box
          sx={{
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            // The one place the portal's own accent appears inside the CRM
            // chrome: a rule along the top of the nameplate, the way a
            // letterhead carries one. See styles/accent.ts for why teal.
            borderTop: 3,
            borderTopColor: (t) => t.palette.accent[500],
            px: { xs: 2.5, sm: 4 },
            py: { xs: 3, sm: 3.5 },
          }}
        >
          <Nameplate name={name} hasLogo={branding?.logoAttached === true} />

          <Box sx={{ mt: { xs: 3, sm: 3.5 }, pt: { xs: 3, sm: 3.5 }, borderTop: 1, borderColor: "divider" }}>
            <Ledger
              pending={isPending}
              figures={[
                {
                  label: "Invoiced",
                  value: money(data?.invoiceTotal ?? null, currency),
                  sub: data?.invoiceCount != null ? `${count(data.invoiceCount)} issued` : null,
                  testId: "ledger-invoiced",
                },
                {
                  label: "Outstanding",
                  value: money(data?.outstandingTotal ?? null, currency),
                  sub:
                    data?.unpaidInvoiceCount != null
                      ? `${count(data.unpaidInvoiceCount)} unpaid`
                      : null,
                  testId: "ledger-outstanding",
                },
                {
                  label: "Overdue",
                  value: money(overdueTotal, currency),
                  sub:
                    data?.overdueInvoiceCount != null
                      ? `${count(data.overdueInvoiceCount)} past due`
                      : null,
                  // Red only for a real, non-zero balance: a reseller who owes
                  // nothing should not have a red figure on their front page.
                  alarming: (overdueTotal ?? 0) > 0,
                  testId: "ledger-overdue",
                },
              ]}
            />
          </Box>
        </Box>
      </Band>

      {/* ── The counts, which are also the doors ────────────────────── */}
      <Band delay={60}>
        <CountTiles
          pending={isPending}
          tiles={[
            {
              label: "Customers",
              value: count(data?.customerCount),
              sub: null,
              to: "/customers",
              testId: "count-customers",
            },
            {
              label: "Invoices",
              value: count(data?.invoiceCount),
              sub:
                data?.paidInvoiceCount != null ? `${count(data.paidInvoiceCount)} paid` : null,
              to: "/invoices",
              testId: "count-invoices",
            },
            {
              label: "Quotes",
              value: count(data?.quoteCount),
              // The count is OPEN quotes only (spec §5): the figure with
              // something to do behind it. "3 quotes" where two were decided
              // months ago is a number nobody acts on.
              sub: "open from XOP Networks",
              to: "/quotes",
              testId: "count-quotes",
            },
          ]}
        />
      </Band>

      {/* ── What needs someone today ────────────────────────────────── */}
      <Band delay={120}>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            alignItems: "start",
          }}
        >
          <OverduePanel
            invoices={data?.overdueInvoices ?? null}
            count={
              data?.overdueInvoiceCount != null ? `${count(data.overdueInvoiceCount)} invoices` : null
            }
            currency={currency}
          />
          <RenewalsPanel licences={licenceError ? null : (licenceData?.licences ?? null)} />
        </Box>
      </Band>
    </Box>
  );
}
